import { Field } from '@base-ui/react/field';
import { Radio } from '@base-ui/react/radio';
import { RadioGroup } from '@base-ui/react/radio-group';
import type { ComponentProps } from 'react';
import { cva, cx, type VariantProps } from '@/cva.config';

const OPTIONS = [
  { label: 'True', value: 'true' },
  { label: 'False', value: 'false' },
];

const booleanFieldVariants = cva({
  base: 'flex',
  variants: {
    layout: {
      column: 'flex-col flex items-start',
      row: 'items-center justify-between',
    },
  },
});

const booleanFieldLabelVariants = cva({
  base: 'text-sm lg:text-base font-medium text-foreground',
  variants: {
    layout: {
      column: 'mb-1.5',
      row: null,
    },
  },
});

export interface BooleanFieldProps
  extends VariantProps<typeof booleanFieldVariants>,
    Omit<
      ComponentProps<typeof RadioGroup>,
      'aria-label' | 'onValueChange' | 'value'
    > {
  label: string;
  onValueChange: (value: boolean) => void;
  value: boolean | undefined;
}

export const BooleanField = ({
  className,
  layout = 'row',
  label,
  onValueChange,
  required = false,
  value,
}: BooleanFieldProps) => (
  <Field.Root className={cx(booleanFieldVariants({ layout }), className)}>
    <Field.Label className={booleanFieldLabelVariants({ layout })}>
      {label}
    </Field.Label>
    <RadioGroup
      aria-label={label}
      className="inline-flex items-center gap-1 rounded-lg border border-border bg-surface-sunken p-0.5"
      onValueChange={(next) => onValueChange(next === 'true')}
      required={required}
      value={(value ?? false) ? 'true' : 'false'}
    >
      {OPTIONS.map((option) => (
        <Radio.Root
          className="cursor-pointer rounded-md px-3 py-1 text-xs lg:text-sm font-medium transition-colors data-checked:bg-accent data-checked:text-white data-unchecked:text-foreground-muted data-unchecked:hover:text-foreground"
          key={option.value}
          value={option.value}
        >
          {option.label}
        </Radio.Root>
      ))}
    </RadioGroup>
  </Field.Root>
);
